import { type ReactNode, useMemo, useState } from 'react'
import { Head, Link } from '@inertiajs/react'
import AdminLayout from '@/components/AdminLayout'
import ResearchNav from '@/components/admin/research/ResearchNav'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Pencil } from 'lucide-react'

type Sponsorship = {
  id: string
  tier: string | null
  amount: string | null
  notes: string | null
  event: {
    id: string
    name: string | null
    date: string | null
    city: string | null
    state: string | null
    organization_name: string
    causes: string[]
  }
}

type Props = {
  sponsor: {
    id: string
    name: string
    website: string | null
    industry: string | null
    notes: string | null
    logo_url: string | null
  }
  sponsorships: Sponsorship[]
  lifetime_total: string
}

const ALL = '__all__'

function formatMoney(value: string | null) {
  if (!value) return '—'
  const num = parseFloat(value)
  if (isNaN(num) || num === 0) return '—'
  return num.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })
}

function formatDate(dateStr: string | null) {
  if (!dateStr) return '—'
  return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

function eventYear(dateStr: string | null): number | null {
  if (!dateStr) return null
  const year = new Date(dateStr + 'T00:00:00').getFullYear()
  return isNaN(year) ? null : year
}

export default function ResearchSponsorShow({ sponsor, sponsorships, lifetime_total }: Props) {
  const [yearFilter, setYearFilter] = useState(ALL)
  const [orgFilter, setOrgFilter] = useState(ALL)
  const [causeFilter, setCauseFilter] = useState(ALL)

  const yearOptions = useMemo(() => {
    const years = new Set<number>()
    sponsorships.forEach(s => {
      const y = eventYear(s.event.date)
      if (y !== null) years.add(y)
    })
    return Array.from(years).sort((a, b) => b - a)
  }, [sponsorships])

  const orgOptions = useMemo(() => {
    const orgs = new Set<string>()
    sponsorships.forEach(s => orgs.add(s.event.organization_name))
    return Array.from(orgs).sort((a, b) => a.localeCompare(b))
  }, [sponsorships])

  const causeOptions = useMemo(() => {
    const causes = new Set<string>()
    sponsorships.forEach(s => s.event.causes.forEach(c => causes.add(c)))
    return Array.from(causes).sort((a, b) => a.localeCompare(b))
  }, [sponsorships])

  const filtered = useMemo(
    () =>
      sponsorships.filter(s => {
        if (yearFilter !== ALL && String(eventYear(s.event.date)) !== yearFilter) return false
        if (orgFilter !== ALL && s.event.organization_name !== orgFilter) return false
        if (causeFilter !== ALL && !s.event.causes.includes(causeFilter)) return false
        return true
      }),
    [sponsorships, yearFilter, orgFilter, causeFilter],
  )

  return (
    <>
      <Head title={`Research - ${sponsor.name}`} />
      <div>
        <h1 className="text-3xl font-semibold tracking-tight font-serif">Research Database</h1>
        <div className="mt-4">
          <ResearchNav />
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {sponsor.logo_url && (
              <img
                src={sponsor.logo_url}
                alt={`${sponsor.name} logo`}
                className="size-12 rounded-lg object-contain ring-1 ring-black/10 bg-card"
              />
            )}
            <h2 className="text-2xl font-semibold tracking-tight">{sponsor.name}</h2>
            {sponsor.industry && <Badge variant="outline">{sponsor.industry}</Badge>}
            <Badge variant="secondary">Lifetime: {formatMoney(lifetime_total)}</Badge>
          </div>
          <Button asChild variant="outline">
            <Link href={`/admin/research/sponsors/${sponsor.id}/edit`}>
              <Pencil className="mr-2 size-4" />
              Edit
            </Link>
          </Button>
        </div>

        {(sponsor.website || sponsor.notes) && (
          <div className="mt-2 space-y-1 text-sm text-muted-foreground">
            {sponsor.website && (
              <a
                href={sponsor.website}
                target="_blank"
                rel="noreferrer"
                className="text-primary hover:underline"
              >
                {sponsor.website}
              </a>
            )}
            {sponsor.notes && <p>{sponsor.notes}</p>}
          </div>
        )}

        <h3 className="mt-8 mb-3 text-lg font-semibold tracking-tight">
          Events sponsored ({filtered.length})
        </h3>

        <div className="mb-3 flex flex-wrap gap-2">
          <Select value={yearFilter} onValueChange={setYearFilter}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="All years" />
            </SelectTrigger>
            <SelectContent position="popper" align="start">
              <SelectGroup>
                <SelectItem value={ALL}>All years</SelectItem>
                {yearOptions.map(year => (
                  <SelectItem key={year} value={String(year)}>
                    {year}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>

          <Select value={orgFilter} onValueChange={setOrgFilter}>
            <SelectTrigger className="w-56">
              <SelectValue placeholder="All organizations" />
            </SelectTrigger>
            <SelectContent position="popper" align="start">
              <SelectGroup>
                <SelectItem value={ALL}>All organizations</SelectItem>
                {orgOptions.map(org => (
                  <SelectItem key={org} value={org}>
                    {org}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>

          <Select value={causeFilter} onValueChange={setCauseFilter}>
            <SelectTrigger className="w-56">
              <SelectValue placeholder="All causes" />
            </SelectTrigger>
            <SelectContent position="popper" align="start">
              <SelectGroup>
                <SelectItem value={ALL}>All causes</SelectItem>
                {causeOptions.map(cause => (
                  <SelectItem key={cause} value={cause}>
                    {cause}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>

        <div className="overflow-hidden rounded-2xl border border-black/10 bg-card shadow-xs">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Event</TableHead>
                <TableHead>Organization</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Location</TableHead>
                <TableHead>Tier</TableHead>
                <TableHead className="text-right">Amount</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map(sponsorship => (
                <TableRow key={sponsorship.id}>
                  <TableCell className="font-medium">
                    <Link
                      href={`/admin/research/events/${sponsorship.event.id}`}
                      className="hover:underline"
                    >
                      {sponsorship.event.name || 'Untitled'}
                    </Link>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {sponsorship.event.organization_name}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {formatDate(sponsorship.event.date)}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {[sponsorship.event.city, sponsorship.event.state].filter(Boolean).join(', ') || '—'}
                  </TableCell>
                  <TableCell>
                    {sponsorship.tier ? <Badge variant="secondary">{sponsorship.tier}</Badge> : '—'}
                  </TableCell>
                  <TableCell className="text-right font-medium">
                    {formatMoney(sponsorship.amount)}
                  </TableCell>
                </TableRow>
              ))}
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="py-8 text-center text-muted-foreground">
                    {sponsorships.length === 0
                      ? 'No sponsorships recorded yet.'
                      : 'No sponsorships match the selected filters.'}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </>
  )
}

ResearchSponsorShow.layout = (page: ReactNode) => <AdminLayout>{page}</AdminLayout>
